import * as Sentry from '@sentry/vue'
import { ToolsClass } from '@/base/class/tools.class'
import {
  isBrowserExtensionError,
  isClick2PaySdkRejection,
  isInjectedDocumentScriptError,
  isValuelessRejection,
} from '@/base/utils/sentryFilters'
import { sanitizeReportData } from '@/base/utils/errorReporting'
import { ROUTING } from '@/base/const/routing.const'

/**
 * Komunikaty wyciszane po TREŚCI. Wszystko, czego nie da się rozpoznać po komunikacie, idzie przez
 * `beforeSend` i predykaty z `sentryFilters.js`.
 */
export const SENTRY_IGNORE_ERRORS = [
  // znany szum z odmontowywania komponentów - nie jest błędem użytkownika
  /__disposer/,

  // Click to Pay: gdy SDK się nie zainicjuje, funkcjonalność sama się wyłącza
  // (`SET_C2P_IS_PROCESS_AVAILABLE` = false), a przyczyna idzie do własnego kanału logów
  // (`logC2PTransaction`). Źródłowy wyciek naprawiony w `store/modules/c2p.js`; te wzorce
  // zostają na to, czego z naszego kodu nie da się złapać - timeouty rzucane WEWNĄTRZ
  // `srcsdk.mastercard.js` i widgetu Visa SRC.
  /Window did not load after \d+ms/,
  /(C2P (Mastercard|Visa) SDK load timeout|Error loading C2P (Mastercard|Visa) SDK)/,
  /Failed to communicate with the iframe/,
  // SDK Mastercard nie doczekało się potwierdzenia `postMessage` do swojego iframe'a. Ta sama
  // historia: rzut powstaje wewnątrz `srcsdk.mastercard.js`, a diagnostyka idzie do
  // `logC2PTransaction`. 92 zdarzenia na dobę (pomiar 13.08.2026).
  /No ack for postMessage mastercard\./,

  // Znany, nieszkodliwy komunikat przeglądarki - nie odpowiada mu żadna awaria.
  /ResizeObserver loop/,

  // ⚠️ Kod WSTRZYKNIĘTY w stronę przez rozszerzenia i przeglądarki wbudowane w aplikacje.
  // Rozszerzenie wykonuje swój skrypt w kontekście dokumentu, więc jego `ReferenceError`-y
  // łapie `window.onerror` Sentry i wyglądają jak nasze. Rozpoznanie po nazwie jest tu pewne:
  // ŻADEN z tych identyfikatorów nie występuje w repozytorium (sprawdzone grepem 13.08.2026),
  // a stos ma klatki `<anonymous>` albo adres dokumentu - `isBrowserExtensionError` ich nie
  // złapie, bo rozszerzenie nie zostawia w stosie własnego pliku. Razem ~104 zdarzenia na dobę.
  /(jQuery is not defined|Can't find variable: jQuery)/,
  /Identifier 'CURRENCY_LIST' has already been declared/,
  /Can't find variable: ProductPageDetector/,
  /HTMLOUT is not defined/,
  // Warstwa blokowania treści wstrzykiwana przez Firefoksa na iOS. Pomiar 17.08.2026: 23 zdarzenia
  // na dobę, `browser.name: Firefox iOS` w 139/139, a cały stos to samo `forEach([native code])` -
  // ani jednej klatki z plikiem, więc `isBrowserExtensionError` nie ma się czego chwycić.
  // W repozytorium nie ma takiej destrukturyzacji (sprawdzone grepem `{ rule }` 17.08.2026).
  /Cannot destructure property 'rule' from null or undefined/,

  // ⚠️ Most natywny in-app browsera (`sendDataToNative`), NIE nasz `MobileAppClass`.
  // Ten komunikat znaczy, że `window.webkit` jest `undefined` - a nasze dwa jedyne odczyty
  // (`tools.class.js` → `isIOS`, `mobileApp.class.js`) są za pełnym łańcuchem strażników
  // `window.webkit && window.webkit.messageHandlers && …`, więc z naszego kodu paść nie może.
  // Zdejmując któryś z tych strażników, zdejmij też ten wzorzec. 35 zdarzeń na dobę.
  /evaluating 'window\.webkit\.messageHandlers'/,

  // Komunikaty pochodzące wprost z API rozszerzeń przeglądarki - powstają, gdy rozszerzenie
  // gada ze swoim workerem, a nie gdy dzieje się cokolwiek na bramce.
  /Invalid call to runtime\.sendMessage/,
  /Object Not Found Matching Id/,

  // ⚠️ AWARIE TRANSPORTU - ten sam stan w trzech różnych komunikatach: `Load failed` (Safari/WebKit),
  // `Failed to fetch` (Chrome/V8), `NetworkError when attempting to fetch resource.` (Firefox).
  // Powstają, gdy żądanie nie doszło do serwera - najczęściej przy opuszczaniu strony, przy zerwanym
  // łączu mobilnym i przy uśpionej karcie. Nie mówią nic o bramce ani o backendzie: odmowę backendu
  // widzimy osobno i dokładniej jako `Error: Bad Request` z `httpStatus` (~260 zdarzeń na dobę,
  // `flow: payment-update` w 901/961 - to jest sygnał actionable).
  //
  // Do 18.08.2026 wyciszony był tylko wariant Safari, więc ta sama awaria była raportowana albo nie,
  // zależnie od przeglądarki użytkownika. Pomiar 18.08.2026: 186 zdarzeń na dobę w 13 grupach
  // (`flow: payment-update` 125, `google-pay` 10, resztę stanowiły odrzucenia bez `.catch`
  // na ścieżkach reklamowych - te NAPRAWIONE w kodzie, nie wyciszone).
  //
  // Wszystkie trzy wzorce są ZAKOTWICZONE, żeby nie połknąć komunikatów, które tylko zawierają
  // ten tekst - w szczególności `TypeError: Failed to fetch dynamically imported module: …`,
  // czyli padnięty import chunka, który jest realnym problemem WebKita
  // (`doc/robocze/webkit-import-chunkow.md`) i musi być widoczny.
  //
  // Świadomy koszt: skali awarii sieci u użytkowników nie zmierzymy z Sentry.
  /^Load failed$/,
  /^Failed to fetch$/,
  /^NetworkError when attempting to fetch resource\.$/,
]

/**
 * Adresy, spod których błędy NIE są nasze, choć rzut następuje w kontekście naszej strony.
 * `denyUrls` porównuje adres z OSTATNIEJ sensownej klatki stosu (SDK pomija `<anonymous>`
 * i `[native code]`), czyli z miejsca, gdzie realnie doszło do rzutu.
 *
 * - `yourcx.min.js` - widget ankietowy wpięty w `public/index.html`. Jego `onMessage` woła
 *   `JSON.parse` na KAŻDEJ wiadomości `postMessage` docierającej do okna, więc wysypuje się na
 *   komunikatach innych skryptów: `JSON Parse error: Unexpected identifier "<token>"`,
 *   21 zdarzeń na dobę, iOS w 189/195 (pomiar 18.08.2026). `ignoreErrors` tu nie zadziała -
 *   token w komunikacie jest zmienny, więc każda wiadomość zakłada w Sentry nową grupę.
 *   Filtry strukturalne też nie: `yourcx.min.js` JEST plikiem `.js` pobranym do strony.
 */
export const SENTRY_DENY_URLS = [/yourcx\.min\.js/]

/**
 * `ignoreErrors` dopasowuje się do KOMUNIKATU, a te cztery filtry muszą patrzeć głębiej: odrzucenia
 * z SDK Click to Pay mają komunikat generyczny (payload w `hint`), a błędów kodu wstrzykniętego
 * w dokument przez rozszerzenia i in-app browsery jest tyle, ile rozszerzeń - ich komunikaty są
 * dowolne i zmieniają się z każdą wersją. Wszystkie cztery rozpoznajemy więc po strukturze zdarzenia.
 * Szczegóły: `sentryFilters.js`.
 *
 * ⚠️ `isValuelessRejection` jest OSTATNI świadomie: trzy filtry przed nim potrafią wskazać źródło
 * zdarzenia, a on wycisza to, czego wskazać się nie da (odrzucenie bez wartości i bez stosu).
 *
 * @param {object} event zdarzenie Sentry
 * @param {object} hint kontekst zdarzenia (m.in. `originalException`)
 * @returns {object|null} zdarzenie do wysłania albo `null`, gdy ma zostać wyciszone
 */
export function beforeSend(event, hint) {
  if (isClick2PaySdkRejection(hint?.originalException)) {
    return null
  }
  if (isInjectedDocumentScriptError(event)) {
    return null
  }
  if (isBrowserExtensionError(event)) {
    return null
  }
  if (isValuelessRejection(event, hint?.originalException)) {
    return null
  }
  return event
}

/**
 * Udział ruchu, z którego zbieramy zdarzenia wydajnościowe (`pageload`, `navigation` i spany
 * z `performanceReporting.js`). Błędów NIE dotyczy - te lecą zawsze.
 *
 * To pierwszy pokrętło do zakręcenia, gdyby instancja zaczęła się dławić: transakcje są
 * kilkakrotnie liczniejsze od błędów (co najmniej jedna na wejście użytkownika), a bramka ma ruch
 * produkcyjny 267 kontekstów. Wolumen sprawdzisz w Sentry: Stats → Performance Units.
 */
export const TRACES_SAMPLE_RATE = 0.1

/**
 * ⚠️ PUSTA LISTA JEST DECYZJĄ, nie niedoróbką - nie wpisuj tu hostów „dla kompletu".
 *
 * `tracePropagationTargets` decyduje, do których żądań SDK DOKŁADA nagłówki `sentry-trace`
 * i `baggage`. Żaden z nich nie jest safelistowany przez CORS, więc dołożenie ich do żądania
 * cross-origin wymusza **preflight OPTIONS** - dokładnie ta pułapka, przez którą fallback REST
 * statusu PIS (`wtn.bm.pl`) rozbijał się `TypeError`-em, zanim GET w ogóle wyszedł
 * (patrz `utils/http-compat.js` i `doc/dokumentacja/architektura.md`, „Warstwa HTTP").
 * Cross-originowe są u nas m.in. `cards.autopay.eu`, `wtn.bm.pl` i adserwery.
 *
 * Tracing po stronie przeglądarki działa bez tego - tracimy wyłącznie sklejenie śladu z backendem.
 * Warunki, na jakich można to włączyć: backend raportuje do tej samej instancji i podchwytuje
 * nagłówek, a host jest same-origin z dokumentem (wtedy CORS nie ma tu nic do rzeczy) - czyli
 * realnie `pay.autopay.eu`. → `doc/dokumentacja/sentry.md` §6
 */
export const SENTRY_TRACE_PROPAGATION_TARGETS = []

/**
 * Nazwa transakcji, której nie dało się sprowadzić do trasy bramki. Jedna dla wszystkich takich
 * przypadków - ścieżka, której nie rozpoznajemy, nie ma prawa zakładać nowej nazwy w Sentry.
 */
export const UNKNOWN_TRANSACTION_NAME = '/*'

/** Pierwsze segmenty ścieżek tras (`start`, `continue`, `secure-back`…) - jedyne, które przepuszczamy. */
const ROUTE_SEGMENTS = new Set(
  Object.values(ROUTING).map((path) => path.split('/').filter(Boolean)[0])
)

/**
 * Sprowadza nazwę transakcji do postaci bez identyfikatorów.
 *
 * ⚠️ `routeLabel: 'name'` NIE WYSTARCZA. Transakcja `pageload` powstaje, zanim router rozstrzygnie
 * nawigację, i instrumentacja nadaje jej wtedy nazwę ze **ścieżki URL-a**; przy wolnym starcie
 * (a przy takim transakcja zdąży się domknąć) ta nazwa zostaje. Pomiar 16.09.2026, godzina po
 * wdrożeniu: 10 nazw na 24 było ścieżką, a 6 z nich niosło surowy `idTransRemote` i `continueHash`
 * (`/web/<kontekst>/continue/<id>/<hash>`) - czyli ~1 % próbkowanych pageloadów zakładało w Sentry
 * osobną nazwę transakcji na płatność. To jest ta sama pułapka, przed którą chroni `routeLabel`,
 * tylko wejściem od drugiej strony, więc sito musi stać tutaj - w ostatnim miejscu przed wysyłką.
 *
 * Identyfikatory zostają w tagu `url` i w `request.url` zdarzenia (tam są świadomie - to jedyny
 * nośnik korelacji z logami backendu). Ta funkcja pilnuje **nazwy**, czyli kardynalności i tego,
 * żeby transakcje jednej trasy liczyły się razem.
 *
 * Kontekst nie wchodzi do nazwy (`/web/*`), bo jest tagiem każdego zdarzenia (`initialScope`) -
 * 267 kontekstów razy kilkanaście tras zrobiłoby z nazwy transakcji drugi, gorszy tag.
 *
 * @param {string} name nazwa transakcji ze zdarzenia
 * @returns {string} nazwa trasy bez zmian albo ścieżka sprowadzona do wzorca
 */
export function normalizeTransactionName(name) {
  // nazwa trasy (`Continue`) i nasze własne pomiary (`payment.paywall.render`) - bez zmian
  if (typeof name !== 'string' || !name.startsWith('/')) {
    return name
  }

  const [prefix, , route, ...params] = name.split('/').filter(Boolean)

  if (prefix !== 'web' || !ROUTE_SEGMENTS.has(route)) {
    return UNKNOWN_TRANSACTION_NAME
  }

  return params.length ? `/web/*/${route}/*` : `/web/*/${route}`
}

/**
 * Sanityzacja zdarzeń WYDAJNOŚCIOWYCH. Zdarzenia błędów czyści wrapper (`errorReporting.js`),
 * ale transakcje omijają go w całości: atrybuty spanów buduje SDK, nie nasz kod.
 *
 * Konkretny powód: instrumentacja routera z `@sentry/vue` przepisuje do atrybutów spanu **wszystkie**
 * parametry i całe query trasy (`params.<klucz>`, `query.<klucz>` w `router.js` pakietu). Dziś są to
 * klucze korelacji (`apiToken`, `hash`, `serviceID`), które świadomie zostawiamy - ale nic nie
 * pilnuje, żeby nowa trasa nie wniosła tam czegoś wrażliwego, a domyślnie poszłoby to do Sentry
 * bez żadnego filtra. Przepuszczamy więc `data` korzenia i każdego spanu przez ten sam sanitizer,
 * co `extra`.
 *
 * Drugie zadanie tej funkcji to **nazwa transakcji** - patrz `normalizeTransactionName`.
 *
 * ⚠️ `data` NIE WYSTARCZY - trzeba wyczyścić też `attributes`. W SDK 7 zdarzenie transakcji niesie
 * w `event.spans` **instancje klasy `Span`**, a nie gotowy JSON (serializacja idzie dopiero przy
 * pakowaniu envelope'u). Taka instancja trzyma dwa osobne worki: starsze `data` i `attributes`
 * (getter na `_attributes`) - a `setAttributes`, którym instrumentacja routera i SDK wpisują swoje
 * pola, trafia **wyłącznie** do tego drugiego. Wyczyszczenie samego `data` byłoby więc połowicznym
 * filtrem, który wygląda na kompletny. `event.contexts.trace` jest odwrotnie - to już zwykły obiekt,
 * w którym oba worki są **scalone** w `data` (patrz `spanToTraceContext`), więc obsługuje go ta sama
 * pętla. Przy podniesieniu SDK do 8.x sprawdź ten kształt ponownie: `event.spans` ma tam być JSON-em.
 *
 * @param {object} event zdarzenie typu `transaction`
 * @returns {object} zdarzenie z wyczyszczonymi atrybutami
 */
export function beforeSendTransaction(event) {
  if (event?.transaction) {
    event.transaction = normalizeTransactionName(event.transaction)
  }

  const spans = [event?.contexts?.trace, ...(event?.spans || [])]
  spans.forEach((span) => {
    if (span?.data) {
      span.data = sanitizeReportData(span.data)
    }
    if (span?.attributes) {
      span.attributes = sanitizeReportData(span.attributes)
    }
  })
  return event
}

/**
 * Konfiguruje Sentry dla bramki. Woływane raz, z `context-init.js`, WYŁĄCZNIE na produkcji -
 * poza nią SDK nie wstaje w ogóle, a `reportError`/`reportMessage` (`errorReporting.js`) są no-opem.
 *
 * SDK jest przypięte do **7.120.4** - wersja dokładna, bez `^`. To ostatnia wersja linii 7.x
 * i **górna granica wyznaczona przez serwer**: SDK 8 i 9 wymagają self-hosted Sentry w wersji
 * 24.4.2 lub wyższej, a `sentry.autopay.eu` to 23.6.1 (potwierdzone nagłówkiem `server:
 * sentry-relay/23.6.1` z ingestu). Podnoszenie SDK do 8.x przed podniesieniem serwera nie ma sensu.
 *
 * Poprzednia instancja (`error.blue.pl`, Sentry 9.x) nie miała endpointu `/envelope/` i trzymała
 * nas na SDK 6.19.7 - stąd brak tracingu, sesji i Replaya. Nowa instancja ma `/store/`,
 * `/envelope/` i `/security/`, wszystkie z `Access-Control-Allow-Origin: *`, więc te ograniczenia
 * odpadły. Szczegóły przepięcia: `doc/dokumentacja/sentry.md` §1.
 *
 * `Sentry.init({ app })` podpina własny `app.config.errorHandler` - nie przypisuj własnego PO tym
 * wywołaniu, bo nadpisze handler Sentry i wyłączy zbieranie błędów komponentów.
 *
 * @param {object} app instancja aplikacji Vue
 * @param {string} contextName nazwa kontekstu (bramki) - tag zdarzeń
 * @param {object} [router] instancja `vue-router` - bez niej transakcje nazywają się ścieżką URL-a
 * @returns {void}
 */
export function initSentry(app, contextName, router) {
  if (!ToolsClass.isProduction()) {
    return
  }

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY,
    environment: import.meta.env.MODE,
    // Identyfikator builda (`<paczka>@<wersja>+<sha|znacznik czasu>`), wstrzykiwany w `vite.config.mjs`.
    // Bez niego „czy poprawka weszła" trzeba było zgadywać z hashy chunków i `Last-Modified` na CDN
    // (`doc/dokumentacja/sentry.md` §8); z nim to filtr `release` na liście zdarzeń.
    release: import.meta.env.VITE_APP_RELEASE,
    // tag kontekstu - bez niego 266 bramek zlewa się w Sentry w jeden projekt
    initialScope: {
      tags: { context: contextName },
    },
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    denyUrls: SENTRY_DENY_URLS,
    // Instrumentacja routera nazywa transakcje NAZWĄ TRASY (`routeLabel: 'name'`), nie ścieżką -
    // inaczej `apiToken` w segmencie URL-a zakładałby nową transakcję na każdą płatność.
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        routeLabel: 'name',
      }),
    ],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    tracePropagationTargets: SENTRY_TRACE_PROPAGATION_TARGETS,
    // Sesje (release health) idą envelope'em - na starej instancji musiały być wyłączone, bo wracały
    // jako 403 z błędem CORS. Tablica `integrations` DOKŁADA się do domyślnych, nie zastępuje ich.
    // Anonimowy rekord „start aplikacji / czy zakończył się awarią" - bez ciasteczka i bez
    // identyfikatora trwałego między wizytami.
    autoSessionTracking: true,
    // Domyślne 3 ucinało `extra` płycej, niż czyści je sanitizer (MAX_DEPTH = 4 w `errorReporting.js`),
    // więc ostatni poziom danych docierał do Sentry jako „[Object]".
    normalizeDepth: 5,
    // Zgodne z MAX_STRING_LENGTH sanitizera - domyślne 250 obcinało nasze własne komunikaty w połowie.
    maxValueLength: 500,
    beforeSend,
    beforeSendTransaction,
  })
}
